<template>
  <div>
    <!-- Combined Uplink Port Information -->
    <div class="section">
      <table class="table align-items-center mb-0">
        <thead>
          <tr>
            <th class="text-sm">Interface</th>
            <th class="text-sm">Link Status</th>
            <th class="text-sm">Speed</th>
            <th class="text-sm">TX Power (dBm)</th>
            <th class="text-sm">RX Power (dBm)</th>
            <th class="text-sm">Bias Current (mA)</th>
            <th class="text-sm">Voltage (V)</th>
            <th class="text-sm">Temperature (°C)</th>
            <th class="text-sm">Vendor</th>
            <th class="text-sm">Rx Bytes</th>
            <th class="text-sm">Tx Bytes</th>
            <th class="text-sm">Errors</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="loading">
            <tr>
              <td colspan="12">
                <Skalaton />
              </td>
            </tr>
          </template>
          <template v-else-if="hasData">
            <tr
              v-for="port in combinedPorts"
              :key="port.portId"
            >
              <td class="text-sm font-weight-bold">{{ port.portId }}</td>
              <td class="text-sm">
                <span class="badge" :class="port.linkStatus === 'Up' ? 'bg-success' : 'bg-danger'" style="text-transform: uppercase; color: rgb(255 249 249) !important;">
                  {{ port.linkStatus }}
                </span>
              </td>
              <td class="text-sm">{{ port.speed }}</td>
              <td class="text-sm" :class="getPowerClass(port.txPower, 'tx')">
                {{ formatPower(port.txPower) }}
              </td>
              <td class="text-sm" :class="getPowerClass(port.rxPower, 'rx')">
                {{ formatPower(port.rxPower) }}
              </td>
              <td class="text-sm">{{ formatCurrent(port.biasCurrent) }}</td>
              <td class="text-sm">{{ formatVoltage(port.voltage) }}</td>
              <td class="text-sm" :class="getTemperatureClass(port.temperature)">
                {{ formatTemperature(port.temperature) }}
              </td>
              <td class="text-sm">{{ port.vendor }}</td>
              <td class="text-sm">{{ formatBytes(port.rxBytes) }}</td>
              <td class="text-sm">{{ formatBytes(port.txBytes) }}</td>
              <td class="text-sm" :class="port.errors > 0 ? 'text-warning' : 'text-success'">
                {{ port.errors }}
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="12" class="text-center text-muted py-4">
                No uplink port data available
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Skalaton from '@/components/Skalaton.vue';

export default {
  name: 'VsolEponUplinkTable',
  components: {
    Skalaton
  },
  props: {
    uplinkInfo: {
      type: Object,
      default: () => ({})
    },
    loading: Boolean
  },
  computed: {
    hasData() {
      return this.uplinkInfo && 
             this.uplinkInfo.uplinkport_status && 
             (this.uplinkInfo.uplinkport_status.optical_transceiver || 
              this.uplinkInfo.uplinkport_status.traffic_statistics);
    },
    combinedPorts() {
      if (!this.hasData) return [];
      
      const opticalData = this.uplinkInfo.uplinkport_status.optical_transceiver || [];
      const trafficData = this.uplinkInfo.uplinkport_status.traffic_statistics || [];
      
      // Get all unique port IDs from both datasets
      const allPortIds = new Set([
        ...opticalData.map(p => p['Port ID']),
        ...trafficData.map(p => p['Port ID'])
      ]);
      
      // Combine data for each port
      return Array.from(allPortIds).map(portId => {
        const opticalPort = opticalData.find(p => p['Port ID'] === portId);
        const trafficPort = trafficData.find(p => p['Port ID'] === portId);
        
        return {
          portId,
          linkStatus: trafficPort ? trafficPort['Link Status'] : 'N/A',
          speed: trafficPort ? trafficPort['Speed'] : 'N/A',
          txPower: opticalPort ? opticalPort['Transmit Power(dBm)'] : 'N/A',
          rxPower: opticalPort ? opticalPort['Received Power(dBm)'] : 'N/A',
          biasCurrent: opticalPort ? opticalPort['Bias Current(mA)'] : 'N/A',
          voltage: opticalPort ? opticalPort['Voltage(V)'] : 'N/A',
          temperature: opticalPort ? opticalPort['Temperature(°C)'] : 'N/A',
          vendor: opticalPort ? opticalPort['Vendor Name'] : 'N/A',
          rxBytes: trafficPort ? parseInt(trafficPort['Rx Bytes']) : 0,
          txBytes: trafficPort ? parseInt(trafficPort['Tx Bytes']) : 0,
          errors: trafficPort ? parseInt(trafficPort['Errors']) : 0,
          collisions: trafficPort ? parseInt(trafficPort['Collisions']) : 0
        };
      }).sort((a, b) => a.portId.localeCompare(b.portId)); // Sort by port ID
    }
  },
  methods: {
    formatPower(value) {
      if (value === null || value === undefined || value === 'N/A') return 'N/A';
      return `${value} dBm`;
    },
    
    formatCurrent(value) {
      if (value === null || value === undefined || value === 'N/A') return 'N/A';
      return `${value} mA`;
    },
    
    formatVoltage(value) {
      if (value === null || value === undefined || value === 'N/A') return 'N/A';
      return `${value} V`;
    },
    
    formatTemperature(value) {
      if (value === null || value === undefined || value === 'N/A') return 'N/A';
      return `${value}°C`;
    },
    
    formatBytes(bytes) {
      if (bytes === 0 || bytes === '0') return '0 B';
      if (typeof bytes === 'string') bytes = parseInt(bytes);
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    getPowerClass(value, type) {
      if (value === null || value === undefined || value === 'N/A') return 'text-muted';
      
      const powerValue = parseFloat(value);
      
      if (type === 'tx') {
        // For uplink TX power (typically SFP modules)
        if (powerValue >= -5 && powerValue <= 0) return 'text-success'; // Excellent uplink TX power
        if (powerValue >= -10 && powerValue < -5) return 'text-warning'; // Warning uplink TX power
        if (powerValue < -10) return 'text-danger'; // Critical uplink TX power
        return 'text-success'; // Default to success for other positive values
      } else {
        // For RX power
        if (powerValue >= -15) return 'text-success'; // Excellent RX power
        if (powerValue >= -25) return 'text-warning'; // Warning RX power
        return 'text-danger'; // Critical RX power
      }
    },
    
    getTemperatureClass(value) {
      if (value === null || value === undefined || value === 'N/A') return 'text-muted';
      
      const tempValue = parseFloat(value);
      if (tempValue < 50) return 'text-success'; // Normal temperature
      if (tempValue < 60) return 'text-warning'; // Warning temperature
      return 'text-danger'; // Critical temperature
    }
  }
}
</script>

<style scoped>
.section-title {
  color: #82D616;
  border-bottom: 1px solid #444;
  padding-bottom: 8px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  font-weight: 600;
}

.table {
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.text-success { color: #28a745 !important; }
.text-warning { color: #ffc107 !important; }
.text-danger { color: #dc3545 !important; }
.text-muted { color: #6c757d !important; }

.font-weight-bold {
  font-weight: 600;
}
</style>